/* ──────────────────────────────────────────────────────────────────────────
 * Service stress prediction — §10.
 *
 * The brief's own framing of the gap is exact:
 *
 *   from  "Toilet usage = 89%"
 *   to    "Sector B sanitation capacity predicted to exceed operational
 *          threshold within 42 minutes because incoming pilgrim flow is
 *          increasing 18%."
 *
 * The first is a gauge. The second is a decision with a clock on it, and the
 * difference between them is entirely the forecast — which only became
 * possible once the platform started moving.
 *
 * So this consumes the live model rather than a static percentage: service
 * demand is derived from the population actually present and the rate it is
 * changing, and the lead time falls out of the same arithmetic.
 *
 * Everything here is simulated. Provision ratios are planning assumptions,
 * not a survey of what is installed at Nashik.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";

/* Provision per 1,000 people, and the service rate each unit sustains.
 *
 * These are the numbers a department replaces with its actual deployment
 * schedule. They are collected here rather than scattered through the
 * calculation so they can be argued with in one place — which is the only
 * way a projected overload is defensible.
 */
/* Each entry must balance: unitsPer1000 × usesPerUnitPerHour has to be of the
 * same order as usesPerPersonPerHour × 1000, or the service reads 0% or 100%
 * for every crowd and the panel tells an operator nothing. The ratio at full
 * sector occupancy is stated in each comment, and a test asserts it stays in
 * a sane band — an unbalanced standard is a silent failure that looks like a
 * working dashboard. */
export const SERVICE_STANDARDS = {
  toilets: {
    label: { en: "Sanitation", mr: "स्वच्छता" },
    /* ~1 unit per 59 people, inside the 1:40–1:100 range Indian mela
     * sanitation planning works to. 0.88 utilisation at full occupancy. */
    unitsPer1000: 17,
    usesPerPersonPerHour: 0.18,
    usesPerUnitPerHour: 12,
    icon: "Toilet",
  },
  water: {
    label: { en: "Drinking water", mr: "पिण्याचे पाणी" },
    /* A standpost serves roughly one person every 18 seconds. In pre-monsoon
     * heat a pilgrim drinks about once an hour. 0.83 at full occupancy. */
    unitsPer1000: 6,
    usesPerPersonPerHour: 1.0,
    usesPerUnitPerHour: 200,
    icon: "Droplets",
  },
  medical: {
    label: { en: "Medical", mr: "वैद्यकीय" },
    /* ~1.5 presentations per 1,000 people per hour at baseline; a post
     * clears about 5 an hour. 0.86 at full occupancy — before heat. */
    unitsPer1000: 0.35,
    usesPerPersonPerHour: 0.0015,
    usesPerUnitPerHour: 5,
    icon: "HeartPulse",
  },
  shelter: {
    label: { en: "Shelter / shade", mr: "निवारा" },
    /* A shade structure holds about 60 people, turning over hourly; a
     * quarter of the crowd seeks shade in an hour. 0.83 at full. */
    unitsPer1000: 5,
    usesPerPersonPerHour: 0.25,
    usesPerUnitPerHour: 60,
    icon: "Building2",
  },
  food: {
    label: { en: "Food / annadan", mr: "अन्नदान" },
    /* A serving counter clears about 90 people an hour; roughly one
     * pilgrim in seven eats within any given hour. 0.88 at full. */
    unitsPer1000: 1.9,
    usesPerPersonPerHour: 0.15,
    usesPerUnitPerHour: 90,
    icon: "UtensilsCrossed",
  },
  parking: {
    label: { en: "Parking", mr: "वाहनतळ" },
    /* Measured in vehicles, not people: about one vehicle per hundred
     * pilgrims arriving each hour, against bays that turn over every four
     * hours. Long dwell is what makes parking fail early. 0.89 at full. */
    unitsPer1000: 45,
    usesPerPersonPerHour: 0.01,
    usesPerUnitPerHour: 0.25,
    icon: "CircleParking",
  },
  transport: {
    label: { en: "Departure transport", mr: "परतीची वाहतूक" },
    /* Departure capacity: a quarter of a sector wants to leave within the
     * hour after a snan, against slots carrying ~55 each. 0.91 at full. */
    unitsPer1000: 5,
    usesPerPersonPerHour: 0.25,
    usesPerUnitPerHour: 55,
    icon: "BusFront",
  },
};

/* Utilisation above which a service is considered under operational stress,
 * and the point at which it has effectively failed. 100% is not the failure
 * threshold: a queue forms well before capacity is reached, and a sanitation
 * queue in a dense crowd is itself a crowd-safety problem. */
export const STRESS_THRESHOLD = 0.8;
export const FAILURE_THRESHOLD = 1.0;

/* Units actually deployed in a sector. Derived from the standard so the demo
 * is internally consistent; a real deployment reads this from the
 * department's asset register. */
function unitsFor(serviceKey, sectorCapacity) {
  const std = SERVICE_STANDARDS[serviceKey];
  return Math.max(1, Math.round((sectorCapacity / 1000) * std.unitsPer1000));
}

/* Utilisation of one service in one sector at a given population. */
function utilisation(serviceKey, people, units) {
  const std = SERVICE_STANDARDS[serviceKey];
  const demandPerHour = people * std.usesPerPersonPerHour;
  const capacityPerHour = units * std.usesPerUnitPerHour;
  return capacityPerHour === 0 ? null : demandPerHour / capacityPerHour;
}

/* ── The §10 output ────────────────────────────────────────────────────
 * For each sector and service: current utilisation, and — where the
 * population is rising — the minutes until the stress threshold is crossed,
 * with the reason stated. */
export function assessServiceStress(liveState, { horizonMinutes = 120 } = {}) {
  const rows = [];

  liveState.sectors.forEach((sector) => {
    Object.keys(SERVICE_STANDARDS).forEach((key) => {
      const units = unitsFor(key, sector.capacity);
      const now = utilisation(key, sector.current, units);
      if (now == null) return;

      /* Project forward at the current net rate. Linear, and labelled as
       * such — arrivals are not linear, but over an operational horizon of
       * an hour or two it is a defensible first approximation and an
       * operator can see the rate it assumed. */
      let minutesToStress = null;
      let minutesToFailure = null;

      if (sector.netPerMin > 0) {
        const std = SERVICE_STANDARDS[key];
        const capacityPerHour = units * std.usesPerUnitPerHour;
        const peopleAtStress =
          (STRESS_THRESHOLD * capacityPerHour) / std.usesPerPersonPerHour;
        const peopleAtFailure =
          (FAILURE_THRESHOLD * capacityPerHour) / std.usesPerPersonPerHour;

        if (sector.current < peopleAtStress) {
          const m = Math.round((peopleAtStress - sector.current) / sector.netPerMin);
          if (m <= horizonMinutes) minutesToStress = m;
        }
        if (sector.current < peopleAtFailure) {
          const m = Math.round((peopleAtFailure - sector.current) / sector.netPerMin);
          if (m <= horizonMinutes) minutesToFailure = m;
        }
      }

      const state =
        now >= FAILURE_THRESHOLD ? "failed"
        : now >= STRESS_THRESHOLD ? "stressed"
        : minutesToStress != null ? "approaching" : "ok";

      rows.push({
        sectorId: sector.id,
        sectorName: sector.name,
        centre: sector.centre,
        service: key,
        label: SERVICE_STANDARDS[key].label,
        units,
        people: sector.current,
        utilisation: Number(now.toFixed(2)),
        utilisationPct: Math.round(now * 100),
        netPerMin: sector.netPerMin,
        minutesToStress,
        minutesToFailure,
        state,
        /* The sentence the brief asked for, assembled from the numbers that
         * produced it rather than written as a template. */
        narrative: buildNarrative(key, sector, now, minutesToStress),
        trust: TRUST.SIMULATED,
      });
    });
  });

  const order = { failed: 0, stressed: 1, approaching: 2, ok: 3 };
  return rows.sort((a, b) => {
    const s = order[a.state] - order[b.state];
    if (s !== 0) return s;
    return (a.minutesToStress ?? 9999) - (b.minutesToStress ?? 9999);
  });
}

function buildNarrative(key, sector, util, minutesToStress) {
  const svc = SERVICE_STANDARDS[key].label.en.toLowerCase();
  const name = sector.name?.en ?? sector.id;

  if (util >= FAILURE_THRESHOLD) {
    return `${name} ${svc} capacity is exceeded — demand is ${Math.round(util * 100)}% of what the deployed units can serve.`;
  }
  if (util >= STRESS_THRESHOLD) {
    return `${name} ${svc} is under stress at ${Math.round(util * 100)}% utilisation; queues will be forming.`;
  }
  if (minutesToStress != null) {
    return `${name} ${svc} capacity predicted to exceed its operational threshold within ${minutesToStress} minutes, because the sector is gaining ${sector.netPerMin} people per minute.`;
  }
  return `${name} ${svc} within capacity at ${Math.round(util * 100)}% utilisation.`;
}

/* Theatre summary for the panel header. */
export function serviceStressSummary(rows) {
  const failed = rows.filter((r) => r.state === "failed");
  const stressed = rows.filter((r) => r.state === "stressed");
  const approaching = rows.filter((r) => r.state === "approaching");
  const soonest = approaching
    .filter((r) => r.minutesToStress != null)
    .sort((a, b) => a.minutesToStress - b.minutesToStress)[0] || null;

  return { failed, stressed, approaching, soonest };
}

export const SERVICE_ASSUMPTIONS = [
  "Provision ratios and per-unit service rates are planning assumptions, not a survey of what is deployed at Nashik.",
  "Projection is linear at the current net arrival rate. Arrivals are not linear — they peak sharply around ritual timings — so treat the lead time as an order of magnitude, not a countdown.",
  "Stress is set at 80% rather than 100%: queues form well before capacity is reached, and a sanitation queue inside a dense crowd is itself a crowd-safety problem.",
  "Demand is assumed uniform across a sector. In practice it concentrates at the nearest facility to the ghat.",
];
